import { useEffect, useState } from "react";
import { ArrowUpRight, Check, MessageCircle, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import VerifiedBadge from "@/components/yts/business/VerifiedBadge";
import ViatorBadge from "@/components/yts/ViatorBadge";
import BookingPartnerLogo from "@/components/yts/BookingPartnerLogo";
import { VIATOR_CARD_BADGE, VIATOR_SOURCE } from "@/lib/viator";
import { Image } from "@/components/ui/image";
import { useSaved } from "@/lib/SavedContext";
import { useDetailOverlay } from "@/lib/DetailOverlayContext";
import ListingPhotoPlaceholder from "@/components/yts/ListingPhotoPlaceholder";
import OpenStatusLine from "@/components/yts/hours/OpenStatusLine";
import { useLang } from "@/lib/i18n";
import { categoryLabel, neighborhoodLabel, listingNameLabel } from "@/lib/locales";
import { isPremium } from "@/data/plans";
import CommunityHeartButton from "@/components/yts/CommunityHeartButton";

/**
 * Dark editorial cards, inspired by neighborhood field guides rather than
 * star-rating marketplaces. The one action on a card puts the place on the
 * visitor's list. Partner experience prices remain intact, with no invented
 * local ratings.
 */
export default function ExperienceCard({ item, priority = false, placeholderTheme = null }) {
  const { t, lang } = useLang();
  const displayTitle = listingNameLabel({ ...item, name: item.title }, lang);
  const displayLabel = categoryLabel(item.label || item.subcategory || item.category || "", lang);
  const displayLocation = neighborhoodLabel(item.location || "", lang);
  const { isSaved, toggleSave } = useSaved();
  const { openListing } = useDetailOverlay();
  const navigate = useNavigate();
  const openFull = () => {
    if (String(item.title || "").trim().toLowerCase() === "mensho") navigate("/mensho");
    else if (/^loop experience emoped rentals/i.test(String(item.title || ""))) navigate("/loopexperience");
    else if (item.path) navigate(item.path);
    else if (item.source === VIATOR_SOURCE) navigate(`/experience/${item.id}`);
    else openListing(item.id, item.source);
  };
  const saved = isSaved(item.id);
  const premiumPresentation = /loop experience/i.test(item.title || item.name || "") || isPremium(item);
  // Editorial photo treatment is separate from the paid Premium/Verified plan.
  const condadoDiningEdit = String(item.title || "").trim().toLowerCase() === "mensho";
  const soon = !!item.coming_soon;
  const isPartner = item.source === VIATOR_SOURCE;
  const isFareHarbor = item.affiliate_provider === "FareHarbor" && !!(item.booking_url || item.primary_action_url);
  const [imageFailed, setImageFailed] = useState(false);
  // Browse cards render just one editorial cover; full galleries belong on detail pages.
  // The dedicated cover wins over a storefront shot, with one safe fallback.
  const coverPhoto = item.cover_image || item.image || item.storefront_image ||
    (Array.isArray(item.photos) ? item.photos.find(Boolean) : "") || "";
  useEffect(() => { setImageFailed(false); }, [coverPhoto]);
  const photoReady = Boolean(coverPhoto) && !imageFailed;
  const tipText = lang === "es" ? "Consejos locales" : "Local tips";
  // Descriptions belong on detail pages; compact browse cards show only
  // what helps a visitor make the next decision: photo, title, location, action.
  const addLabel = saved ? (lang === "es" ? "Guardado" : "Saved") : (lang === "es" ? "Guardar" : "Save");

  return (
    <article className={`yts-premium-card group flex h-full min-w-0 flex-col overflow-hidden rounded-[2px] border text-foreground transition-colors duration-300 ${condadoDiningEdit ? "border-[#c5b8a1] bg-[#faf8f2] dark:border-[#8e806b] dark:bg-[#24221f]" : "border-[#e6e2da] bg-white hover:border-[#a5a099] dark:border-white/15 dark:bg-[#21211f] dark:hover:border-white/40"}`}>
      <div className="relative">
        <button
          type="button"
          onClick={openFull}
          aria-label={lang === "es" ? "Abrir " + displayTitle : `Open ${displayTitle}`}
          className="relative block w-full overflow-hidden text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d44b40] focus-visible:ring-inset"
        >
          <div className="relative aspect-[5/4] w-full overflow-hidden bg-[#2d2c2b]">
            {photoReady ? (
              <Image
                src={coverPhoto}
                alt={displayTitle}
                fittingType="fill"
                quality={82}
                sizes="(max-width: 640px) 78vw, 320px"
                loading={priority ? "eager" : "lazy"}
                fetchPriority={priority ? "high" : "low"}
                decoding="async"
                onError={() => setImageFailed(true)}
                className="h-full w-full object-cover transition-transform duration-700 ease-out motion-safe:group-hover:scale-[1.045]"
              />
            ) : (
              <ListingPhotoPlaceholder item={item} lang={lang} theme={placeholderTheme} />
            )}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/20 to-transparent" />
          </div>
        </button>
        {/* Community hearts belong to local places, not third-party bookable tours. */}
        {!soon && !isPartner && !isFareHarbor && item.id && (
          <div className="absolute right-2 top-2 z-20">
            <CommunityHeartButton listingId={item.id} overlay />
          </div>
        )}
        <div className={`pointer-events-none absolute left-2 top-2 z-10 ${isPartner || isFareHarbor ? "max-w-[calc(100%-54px)]" : "max-w-[calc(100%-90px)]"}`}>
          {isPartner ? (
            <ViatorBadge label={VIATOR_CARD_BADGE} />
          ) : isFareHarbor ? (
            <span className="inline-flex min-h-[25px] items-center"><BookingPartnerLogo provider="fareharbor" /></span>
          ) : null}
        </div>

      </div>

      <div className="flex min-w-0 flex-1 flex-col px-3.5 pb-3 pt-3">
        <p className="yts-card-kicker mb-1.5 min-h-3 truncate text-[9px] font-extrabold uppercase tracking-[.105em] text-[#a64239] dark:text-[#eeaaa0]">
          {isPartner || isFareHarbor ? (lang === "es" ? "EXPERIENCIA EN PUERTO RICO" : "PUERTO RICO EXPERIENCE") : (soon ? t("detail.comingSoon") : condadoDiningEdit ? (lang === "es" ? "RAMEN JAPONÉS" : "JAPANESE RAMEN") : displayLabel || (lang === "es" ? "LUGAR LOCAL" : "LOCAL PLACE"))}
        </p>
        <button type="button" onClick={openFull} className="rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/60">
          <h3 className="line-clamp-2 min-h-[2.26em] break-words text-[17px] font-extrabold leading-[1.13] tracking-[-0.035em] text-foreground [overflow-wrap:anywhere] sm:text-[19px]">
            {displayTitle}
          </h3>
        </button>

        <div className="mt-1.5 flex min-w-0 items-center gap-1.5 text-[11px] font-medium leading-[1.45] tracking-[.002em] text-muted-foreground">
          <span className="min-w-0 truncate">{displayLocation || (lang === "es" ? "Puerto Rico" : "Puerto Rico")}</span>
          {item.verified && premiumPresentation && <VerifiedBadge size="sm" label="" className="shrink-0" />}
        </div>

        {(item.editor_pick || item.locals_know) && !isPartner && (
          <p className="mt-1.5 text-[9px] font-black uppercase tracking-[0.11em] text-[#ae3440] dark:text-[#f5a0a7]">
            {item.editor_pick ? (lang === "es" ? "SELECCIÓN EDITORIAL" : "EDITORIAL PICK") : (lang === "es" ? "EN NUESTRA GUÍA LOCAL" : "IN OUR LOCAL EDIT")}
          </p>
        )}
        <div className="mt-auto border-t border-[#e5e1da] pt-2.5 dark:border-white/15">
          {isPartner && item.price && <p className="pb-1.5 text-[11.5px] font-extrabold text-foreground">{item.price}</p>}
          {!soon && !isPartner && !isFareHarbor && (
            <div className="min-w-0 pb-1.5 text-muted-foreground">
              <OpenStatusLine listing={item} compact />
            </div>
          )}
          <div className="flex min-h-9 items-center justify-between gap-2">
            {soon ? (
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">{t("detail.comingSoon")}</span>
            ) : (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggleSave(item.id); }}
                aria-pressed={saved}
                aria-label={lang === "es"
                  ? (saved ? "Quitar de la lista " : "Añadir a la lista ") + displayTitle
                  : (saved ? `Remove ${displayTitle} from your list` : `Add ${displayTitle} to your list`)}
                className={`inline-flex min-h-8 min-w-0 max-w-[64%] items-center gap-1 rounded-[2px] border px-2.5 py-1.5 text-[9px] font-extrabold uppercase leading-none tracking-[0.085em] transition active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/60 ${saved ? "border-[#fb6975]/60 bg-[#fb6975]/15 text-[#c9182c]" : "border-foreground/20 bg-transparent text-foreground hover:border-foreground/50 hover:bg-foreground/[0.05]"}`}
              >
                {saved ? <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={2.4} /> : <Plus className="h-3.5 w-3.5 shrink-0" strokeWidth={2.4} />}
                <span className="truncate">{addLabel}</span>
              </button>
            )}
            {!soon && (isPartner ? (
              <button type="button" onClick={openFull} className="inline-flex shrink-0 items-center gap-1 text-[11px] font-extrabold text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/60">
                {lang === "es" ? "Ver y reservar" : "View & book"} <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            ) : isFareHarbor ? (
              <a href={item.booking_url || item.primary_action_url} onClick={(event) => event.stopPropagation()} className="inline-flex shrink-0 items-center text-[11px] font-extrabold underline underline-offset-2 text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/60">{lang === "es" ? "Consultar fechas" : "Check dates"}</a>
            ) : (
              <button
                type="button"
                onClick={openFull}
                className="inline-flex shrink-0 items-center gap-1 text-[10.5px] font-semibold text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/60"
              >
                <MessageCircle className="h-[14px] w-[14px]" strokeWidth={1.7} />
                <span>{tipText}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
}